/**
 * OneQrew Material-UI Theme
 *
 * Konfiguriert das MUI Theme mit OneQrew Brand Guidelines
 */

import { createTheme } from '@mui/material/styles';
import {
  colors,
  colorSchemeLight,
  colorSchemeDark,
  interactionStates,
  typography as typographyTokens,
  spacing as spacingTokens,
  borderRadius,
  shadows as shadowTokens,
  breakpoints as breakpointTokens,
} from './tokens';

/**
 * OneQrew Light Theme (Standard)
 */
export const lightTheme = createTheme({
  palette: {
    mode: 'light',

    // Primary (Purple)
    primary: {
      main: colorSchemeLight.primaryContainer,
      contrastText: colorSchemeLight.onPrimaryContainer,
      light: colors.purpleShades.light,
      dark: colors.purpleShades.dark,
    },

    // Secondary (Yellow Accent)
    secondary: {
      main: colorSchemeLight.primaryContainerAlt,
      contrastText: colorSchemeLight.onPrimaryContainerAlt,
    },

    // Error
    error: {
      main: colorSchemeLight.error,
      contrastText: colorSchemeLight.onError,
    },

    // Warning
    warning: {
      main: colorSchemeLight.warning,
      contrastText: colorSchemeLight.onWarning,
    },

    // Success
    success: {
      main: colorSchemeLight.success,
      contrastText: colorSchemeLight.onSuccess,
    },

    // Background
    background: {
      default: colorSchemeLight.secondarySurface, // #F4F3EE Light Grey für bessere Card-Hierarchie
      paper: colorSchemeLight.primarySurface, // #FFFFFF Weiß für Cards
    },

    // Text
    text: {
      primary: colorSchemeLight.onPrimarySurface,
      secondary: colorSchemeLight.onSecondarySurface,
      disabled: colorSchemeLight.onDisabledContainer,
    },

    // Divider
    divider: colors.greyShades.medium,

    // Custom OneQrew Colors
    oneqrew: {
      purple: colors.primary.purple,
      yellow: colors.accent.yellow,
      lightGrey: colors.secondary.lightGrey,

      // ColorScheme Light
      ...colorSchemeLight,

      // States
      focus: interactionStates.focussed,
      stateHovered: interactionStates.stateLayer.hovered,
      stateSelected: interactionStates.stateLayer.selected,
      statePressed: interactionStates.stateLayer.pressed,
    },
  },

  typography: {
    fontFamily: typographyTokens.fontFamily.primary,

    // Headlines (Desktop-first, responsive via breakpoints)
    h1: {
      ...typographyTokens.desktop.h1,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.h1,
      },
    },
    h2: {
      ...typographyTokens.desktop.h2,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.h2,
      },
    },
    h3: {
      ...typographyTokens.desktop.h3,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.h3,
      },
    },
    h4: {
      ...typographyTokens.desktop.h4,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.h4,
      },
    },
    h5: {
      fontSize: '20px',
      fontWeight: typographyTokens.fontWeights.medium,
      lineHeight: 1.4,
      '@media (max-width:960px)': {
        fontSize: '18px',
      },
    },
    h6: {
      fontSize: '18px',
      fontWeight: typographyTokens.fontWeights.medium,
      lineHeight: 1.4,
      '@media (max-width:960px)': {
        fontSize: '16px',
      },
    },

    // Body Text
    body1: {
      ...typographyTokens.desktop.body,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.body,
      },
    },
    body2: {
      fontSize: '16px',
      fontWeight: typographyTokens.fontWeights.book,
      lineHeight: 1.5,
      '@media (max-width:960px)': {
        fontSize: '14px',
      },
    },

    // Intro Text (Subtitle)
    subtitle1: {
      ...typographyTokens.desktop.intro,
      '@media (max-width:960px)': {
        ...typographyTokens.mobile.intro,
      },
    },
    subtitle2: {
      fontSize: '20px',
      fontWeight: typographyTokens.fontWeights.book,
      lineHeight: 1.5,
      '@media (max-width:960px)': {
        fontSize: '18px',
      },
    },

    // Button Text
    button: {
      fontSize: '16px',
      fontWeight: typographyTokens.fontWeights.medium,
      textTransform: 'none',
      letterSpacing: '0.02em',
    },

    // Caption
    caption: {
      fontSize: '14px',
      fontWeight: typographyTokens.fontWeights.book,
      lineHeight: 1.4,
    },

    // Overline
    overline: {
      fontSize: '12px',
      fontWeight: typographyTokens.fontWeights.medium,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
  },

  spacing: spacingTokens.unit, // 8px als Basis-Einheit

  shape: {
    borderRadius: parseInt(borderRadius.xxs), // 8px als Standard
  },

  shadows: [
    shadowTokens.none,
    shadowTokens.sm,
    shadowTokens.sm,
    shadowTokens.md,
    shadowTokens.md,
    shadowTokens.md,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
    shadowTokens.xl,
  ],

  breakpoints: {
    values: breakpointTokens,
  },

  components: {
    // ========================================================================
    // MUI BUTTON OVERRIDES
    // ========================================================================
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xxs, // 8px für Buttons
          padding: `${spacingTokens.sm}px ${spacingTokens.md}px`, // 12px 24px
          fontSize: '16px',
          fontWeight: typographyTokens.fontWeights.medium,
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',

          // Fokus-Ring (Accessibility)
          '&:focus-visible': {
            outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
            outlineOffset: interactionStates.focusRingOffset,
          },
        },

        // Contained Button (MainPurple / MainYellow)
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: shadowTokens.sm,
            backgroundColor: colorSchemeLight.primaryContainer,
            backgroundImage: `linear-gradient(${interactionStates.stateLayer.hovered}, ${interactionStates.stateLayer.hovered})`,
          },
          '&:active': {
            boxShadow: shadowTokens.sm,
            backgroundImage: `linear-gradient(${interactionStates.stateLayer.pressed}, ${interactionStates.stateLayer.pressed})`,
          },
          '&.Mui-disabled': {
            backgroundColor: colorSchemeLight.disabledContainer,
            color: colorSchemeLight.onDisabledContainer,
          },
        },

        // Outlined Button (Secondary)
        outlined: {
          borderWidth: '2px',
          borderColor: colorSchemeLight.onSecondaryContainer,
          backgroundColor: colorSchemeLight.secondaryContainer,
          color: colorSchemeLight.onSecondaryContainer,
          '&:hover': {
            borderWidth: '2px',
            borderColor: colorSchemeLight.onSecondaryContainer,
            backgroundColor: colorSchemeLight.secondaryContainer,
            backgroundImage: `linear-gradient(${interactionStates.stateLayer.hovered}, ${interactionStates.stateLayer.hovered})`,
          },
          '&:active': {
            borderWidth: '2px',
            backgroundImage: `linear-gradient(${interactionStates.stateLayer.pressed}, ${interactionStates.stateLayer.pressed})`,
          },
          '&.Mui-disabled': {
            borderColor: colorSchemeLight.onDisabledContainer,
            color: colorSchemeLight.onDisabledContainer,
          },
        },

        // Text Button (Tertiary)
        text: {
          color: colorSchemeLight.onTertiaryContainer,
          '&:hover': {
            backgroundColor: interactionStates.stateLayer.hovered,
          },
          '&:active': {
            backgroundColor: interactionStates.stateLayer.pressed,
          },
          '&.Mui-disabled': {
            color: colorSchemeLight.onDisabledContainer,
          },
        },

        // Size Variants
        sizeSmall: {
          padding: `${spacingTokens.xs}px ${spacingTokens.sm}px`, // 8px 16px
          fontSize: '14px',
          borderRadius: borderRadius.xxs, // 8px
        },
        sizeLarge: {
          padding: `${spacingTokens.sm}px ${spacingTokens.lg}px`, // 16px 32px
          fontSize: '18px',
          borderRadius: borderRadius.xxs, // 8px auch für Large
        },
      },
    },

    // ========================================================================
    // MUI CARD OVERRIDES
    // ========================================================================
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xxs, // 8px Standard für Cards
          boxShadow: shadowTokens.sm,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: shadowTokens.md,
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.s, // 24px
        },
        rounded: {
          borderRadius: borderRadius.s,
        },
      },
    },

    // ========================================================================
    // MUI CHIP OVERRIDES
    // ========================================================================
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.s, // 24px
          fontWeight: typographyTokens.fontWeights.medium,
        },
      },
    },

    // ========================================================================
    // MUI TEXTFIELD OVERRIDES
    // ========================================================================
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.xxs, // 8px
            '&:focus-within': {
              outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
              outlineOffset: interactionStates.focusRingOffset,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xxs, // 8px
        },
      },
    },

    // ========================================================================
    // MUI ICON BUTTON OVERRIDES
    // ========================================================================
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: interactionStates.stateLayer.hovered,
          },
          '&:active': {
            backgroundColor: interactionStates.stateLayer.pressed,
          },
          '&:focus-visible': {
            outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
            outlineOffset: interactionStates.focusRingOffset,
          },
        },
      },
    },

    // ========================================================================
    // MUI LIST OVERRIDES
    // ========================================================================
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xxs, // 8px
          '&.Mui-selected': {
            backgroundColor: interactionStates.stateLayer.selected,
            '&:hover': {
              backgroundColor: interactionStates.stateLayer.hovered,
            },
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xxs,
          '&:hover': {
            backgroundColor: interactionStates.stateLayer.hovered,
          },
          '&.Mui-selected': {
            backgroundColor: interactionStates.stateLayer.selected,
          },
          '&:focus-visible': {
            outline: `${interactionStates.focusRingWidth} solid ${interactionStates.focussed}`,
            outlineOffset: interactionStates.focusRingOffset,
          },
        },
      },
    },

    // ========================================================================
    // MUI DIVIDER OVERRIDES
    // ========================================================================
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.greyShades.medium,
        },
      },
    },

    // ========================================================================
    // MUI APPBAR OVERRIDES
    // ========================================================================
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadowTokens.sm,
          backgroundColor: colorSchemeLight.primarySurface,
          color: colorSchemeLight.onPrimarySurface,
        },
      },
    },

    // ========================================================================
    // MUI DRAWER OVERRIDES
    // ========================================================================
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colorSchemeLight.primarySurface,
          borderRight: `1px solid ${colors.greyShades.medium}`,
        },
      },
    },
  },
});

/**
 * OneQrew Dark Theme
 */
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',

    // Primary (Yellow auf Dark)
    primary: {
      main: colorSchemeDark.primaryContainer,
      contrastText: colorSchemeDark.onPrimaryContainer,
    },

    // Secondary (Purple auf Dark)
    secondary: {
      main: colorSchemeDark.primaryContainerAlt,
      contrastText: colorSchemeDark.onPrimaryContainerAlt,
    },

    // Error
    error: {
      main: colorSchemeDark.error,
      contrastText: colorSchemeDark.onError,
    },

    // Warning
    warning: {
      main: colorSchemeDark.warning,
      contrastText: colorSchemeDark.onWarning,
    },

    // Success
    success: {
      main: colorSchemeDark.success,
      contrastText: colorSchemeDark.onSuccess,
    },

    // Background
    background: {
      default: colorSchemeDark.primarySurface,
      paper: colorSchemeDark.secondarySurface,
    },

    // Text
    text: {
      primary: colorSchemeDark.onPrimarySurface,
      secondary: colorSchemeDark.onSecondarySurface,
      disabled: colorSchemeDark.onDisabledContainer,
    },

    // Divider
    divider: colors.purpleShades.medium,

    // Custom OneQrew Colors
    oneqrew: {
      purple: colors.primary.purple,
      yellow: colors.accent.yellow,
      lightGrey: colors.secondary.lightGrey,

      // ColorScheme Dark
      ...colorSchemeDark,

      // States
      focus: interactionStates.focussed,
      stateHovered: interactionStates.stateLayer.hovered,
      stateSelected: interactionStates.stateLayer.selected,
      statePressed: interactionStates.stateLayer.pressed,
    },
  },

  typography: lightTheme.typography,
  spacing: lightTheme.spacing,
  shape: lightTheme.shape,
  shadows: lightTheme.shadows,
  breakpoints: lightTheme.breakpoints,
  components: {
    ...lightTheme.components,

    // Dark Theme spezifische Overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colorSchemeDark.primarySurface,
          color: colorSchemeDark.onPrimarySurface,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colorSchemeDark.primarySurface,
          borderRight: `1px solid ${colors.purpleShades.medium}`,
        },
      },
    },
  },
});

// Standard Export (Light Theme)
export default lightTheme;
